import React, { useState } from 'react';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';

interface IDescriptionSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
    onSave?: () => Promise<boolean>;
}

const DescriptionSection: React.FC<IDescriptionSectionProps> = ({ leaseMaster, onChange, onSave }) => {
    // This prop determines if the component can *ever* be edited.
    const isPotentiallyEditable = !!onChange;

    // This local state controls whether the component is *currently* in editing mode.
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (value: string) => {
        if (onChange) {
            onChange('legalDescription', value);
        }
    };

    const handleToggleEdit = async (): Promise<void> => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        setIsSaving(true);
        const wasSaved = (await onSave?.()) ?? true;
        setIsSaving(false);
        if (wasSaved) {
            setIsEditing(false);
        }
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                    </svg>
                    Legal Description
                </span>

                {/* The Edit/Done button only appears if the component can be edited */}
                {isPotentiallyEditable && (
                    <button
                        onClick={handleToggleEdit}
                        disabled={isSaving}
                        style={isEditing ? styles.buttonSuccess : styles.buttonSecondary} // Style changes based on state
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    >
                        {isEditing ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        )}
                        <span style={{ marginLeft: '0.5rem' }}>{isSaving ? 'Saving...' : isEditing ? 'Done' : 'Edit'}</span>
                    </button>
                )}
            </h2>

            {/* Empty state logic is unchanged */}
            {!leaseMaster.legalDescription && !isPotentiallyEditable ? (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <path d="M9 11H15" />
                        <path d="M12 8V14" />
                        <circle cx="12" cy="12" r="10" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600 }}>No legal description available</p>
                </div>
            ) : (
                <div style={{ position: 'relative' }}>
                    <textarea
                        value={leaseMaster.legalDescription || ''}
                        disabled={!isEditing} // Textarea is disabled when not in editing mode
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={isEditing ? 'Enter legal description...' : 'Click "Edit" to make changes'}
                        autoComplete="off"
                        onFocus={(e) => {
                            if (isEditing) {
                                e.currentTarget.style.borderColor = styles.colors.borderFocus;
                                e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
                            }
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = styles.colors.gray300;
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                        style={{
                            // Style now depends on the 'isEditing' state, not the old prop
                            ...(!isEditing ? { ...styles.textareaBase, ...styles.inputDisabled } : styles.textareaBase),
                            minHeight: '150px', // Reduced height to minimize scrolling
                            lineHeight: '1.75',
                            fontFamily: styles.typography.fontFamily,
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'normal',
                            wordBreak: 'normal',
                        }}
                    />
                    {/* Character count logic is unchanged */}
                    {leaseMaster.legalDescription && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '1rem',
                                right: '1rem',
                                fontSize: styles.typography.fontSize.sm,
                                color: styles.colors.gray500,
                                pointerEvents: 'none',
                            }}
                        >
                            {leaseMaster.legalDescription.length} characters
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DescriptionSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Description.tsx